import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';

const STATUS_META = {
  not_started: { label: 'Not Started', chip: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'In Progress', chip: 'bg-blue-50 text-blue-800 border-blue-200' },
  submitted: { label: 'Submitted', chip: 'bg-violet-50 text-violet-800 border-violet-200' },
  cleared: { label: 'Cleared', chip: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', chip: 'bg-rose-50 text-rose-800 border-rose-200' },
};
const STATUSES = ['not_started', 'in_progress', 'submitted', 'cleared', 'rejected'];
const URGENCY = {
  overdue: { label: 'Overdue', cls: 'text-rose-700 font-bold', icon: AlertTriangle },
  today: { label: 'Due today', cls: 'text-amber-700 font-bold', icon: Clock },
  this_week: { label: 'This week', cls: 'text-amber-600', icon: Clock },
  future: { label: 'Upcoming', cls: 'text-slate-500', icon: Clock },
};

export default function PQTracker() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dueWeek, setDueWeek] = useState(false);

  const load = async () => {
    try { setData(await apiGet('/bd/pq-tracker')); }
    catch { toast.error('Could not load PQ tracker'); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    let items = data?.items || [];
    if (statusFilter) items = items.filter((i) => i.pq_status === statusFilter);
    if (dueWeek) items = items.filter((i) => ['overdue', 'today', 'this_week'].includes(i.urgency));
    return items;
  }, [data, statusFilter, dueWeek]);

  const markSubmitted = async (id) => {
    try { await apiPut(`/crm/opportunities/${id}/pq`, { pq_status: 'submitted' }); toast.success('Marked submitted'); load(); }
    catch { toast.error('Could not update'); }
  };
  const changeStatus = async (id, status) => {
    try { await apiPut(`/crm/opportunities/${id}/pq`, { pq_status: status }); load(); }
    catch { toast.error('Could not update status'); }
  };

  const sc = data?.status_counts || {};
  const uc = data?.urgency_counts || {};

  return (
    <div data-testid="pq-tracker-page">
      <PageHeader overline="Business Development" title="Pre-Qualification Tracker"
        subtitle="Track enquiries that need a PQ submission — deadlines and reminders only. No document vault." />

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-xs px-3 py-1.5 rounded-md bg-white border border-slate-200" data-testid="pq-count-total"><b>{data?.total ?? 0}</b> total</span>
        {uc.overdue > 0 && <span className="text-xs px-3 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-800" data-testid="pq-count-overdue"><b>{uc.overdue}</b> overdue</span>}
        {uc.today > 0 && <span className="text-xs px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800"><b>{uc.today}</b> due today</span>}
        {uc.this_week > 0 && <span className="text-xs px-3 py-1.5 rounded-md bg-amber-50 border border-amber-100 text-amber-700"><b>{uc.this_week}</b> this week</span>}
        <span className="text-xs px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800"><b>{sc.in_progress || 0}</b> in progress</span>
        <span className="text-xs px-3 py-1.5 rounded-md bg-violet-50 border border-violet-200 text-violet-800"><b>{sc.submitted || 0}</b> submitted</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-md px-2.5 py-1.5 text-xs" data-testid="pq-filter-status">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <button onClick={() => setDueWeek((v) => !v)} className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${dueWeek ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'text-slate-600 border-slate-300 hover:bg-slate-100'}`} data-testid="pq-filter-dueweek">
          Due this week
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <table className="w-full dense-table" data-testid="pq-table">
          <thead>
            <tr><th>Client</th><th>Project</th><th>PQ Deadline</th><th>Days Left</th><th>Status</th><th>Owner</th><th></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="7" className="text-center py-10 text-slate-400 text-xs uppercase tracking-widest font-display" data-testid="pq-empty">No PQ-required opportunities. Mark an opportunity <strong>PQ Required</strong> to track it here.</td></tr>
            )}
            {rows.map((r) => {
              const u = URGENCY[r.urgency] || {};
              const UIcon = u.icon;
              return (
                <tr key={r.id} data-testid={`pq-row-${r.id}`}>
                  <td className="font-display font-semibold">{r.client || '—'}</td>
                  <td className="cursor-pointer hover:text-[#3b4872]" onClick={() => navigate(`/app/crm/opportunities`)}>{r.project_name || '—'}</td>
                  <td className="text-xs font-mono">{(r.pq_submission_deadline || '—').slice(0, 10)}</td>
                  <td className={`text-xs ${u.cls || ''}`}>
                    {r.days_left == null ? '—' : (
                      <span className="flex items-center gap-1">{UIcon && <UIcon size={11} />}{r.days_left < 0 ? `${Math.abs(r.days_left)}d overdue` : `${r.days_left}d`}</span>
                    )}
                  </td>
                  <td>
                    <select value={r.pq_status} onChange={(e) => changeStatus(r.id, e.target.value)} className={`tag border ${STATUS_META[r.pq_status]?.chip || ''} cursor-pointer`} data-testid={`pq-status-${r.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                    </select>
                  </td>
                  <td className="text-xs">{r.owner || '—'}</td>
                  <td className="text-right">
                    {!['submitted', 'cleared', 'rejected'].includes(r.pq_status) && (
                      <button onClick={() => markSubmitted(r.id)} className="text-xs font-display font-bold tracking-wider uppercase text-[#3b4872] hover:text-[#0F172A] flex items-center gap-1 ml-auto" data-testid={`pq-mark-submitted-${r.id}`}>
                        <ShieldCheck size={12} /> Mark Submitted
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
